import {
  CollapsableStep,
  StripeCardElement,
  StripeSplitCardFields,
} from "widgets/booker/shared";
import { Flag, Input, Switch, Text } from "@ttc3k/trekker";
import { usePaymentMethod } from "./usePaymentMethod";
import { useTranslation } from "react-i18next";
import { Flex, styled } from "styled-system/jsx";
import { useBookerContext } from "widgets/booker/contexts";
export const PaymentMethod = () => {
  const {
    onChange,
    onSplitCardChange,
    optionalCardPostalCode,
    defaultPostalCode,
    isComplete,
    isDisabled,
    acceptCard,
    acceptDirectDebit,
    acceptBankTransfer,
    acceptOfflinePayment,
    methodLabels,
    isPadOverLimit,
    selectedPaymentType,
    onPaymentTypeChange,
    stripeRegion,
    checkoutDescription,
    offerTaxExemption,
    taxExempt,
    taxExemptionNumber,
    isTaxExemptionNumberValid,
    onTaxExemptToggle,
    onTaxExemptionNumberChange,
  } = usePaymentMethod();

  const { t } = useTranslation("booker", { keyPrefix: "PAYMENT_METHOD" });
  const { booker } = useBookerContext();

  if (!booker?.paymentMetadata?.connectedAccountId) {
    return (
      <CollapsableStep title={t("TITLE")} complete={false} stepId="payment-method">
        <Flag
          visual="warning"
          hideCloseTrigger
          text={t("NO_ACCOUNT_PLACEHOLDER")}
        />
      </CollapsableStep>
    );
  }

  const enabledMethodCount = [
    acceptCard,
    acceptDirectDebit,
    acceptBankTransfer,
    acceptOfflinePayment,
  ].filter(Boolean).length;
  const showSelector = enabledMethodCount >= 2;

  const labels = {
    card: methodLabels.card || t("PAY_BY_CARD"),
    direct_debit:
      methodLabels.direct_debit ||
      (stripeRegion === "CA"
        ? t("PAY_BY_DIRECT_DEBIT_CA")
        : t("PAY_BY_DIRECT_DEBIT_US")),
    bank_transfer: methodLabels.bank_transfer || t("PAY_BY_BANK_TRANSFER"),
    offline_payment:
      methodLabels.offline_payment || t("PAY_BY_OFFLINE_PAYMENT"),
  };

  return (
    <CollapsableStep
      title={t("TITLE")}
      complete={isComplete}
      stepId="payment-method"
    >
      <Text color="text.mid">{t("SUBTITLE")}</Text>

      {checkoutDescription && (
        <Flex
          p="200"
          borderRadius="100"
          border="1px solid"
          borderColor="border.light"
          bg="ink.100"
        >
          <Text color="text.mid">{checkoutDescription}</Text>
        </Flex>
      )}

      {showSelector && (
        <Flex gap="100" w="full">
          {acceptCard && (
            <PaymentTypeButton
              active={selectedPaymentType === "card"}
              onClick={() => onPaymentTypeChange("card")}
              label={labels.card}
            />
          )}
          {acceptDirectDebit && (
            <PaymentTypeButton
              active={selectedPaymentType === "direct_debit"}
              onClick={() => onPaymentTypeChange("direct_debit")}
              label={labels.direct_debit}
              disabled={isPadOverLimit && stripeRegion === "CA"}
            />
          )}
          {acceptBankTransfer && (
            <PaymentTypeButton
              active={selectedPaymentType === "bank_transfer"}
              onClick={() => onPaymentTypeChange("bank_transfer")}
              label={labels.bank_transfer}
            />
          )}
          {acceptOfflinePayment && (
            <PaymentTypeButton
              active={selectedPaymentType === "offline_payment"}
              onClick={() => onPaymentTypeChange("offline_payment")}
              label={labels.offline_payment}
            />
          )}
        </Flex>
      )}

      {selectedPaymentType === "card" &&
        acceptCard &&
        (optionalCardPostalCode ? (
          <StripeSplitCardFields
            onChange={onSplitCardChange}
            isDisabled={isDisabled}
            stripeRegion={stripeRegion}
            defaultPostalCode={defaultPostalCode}
            requirePostalCode={false}
            labels={{
              cardNumber: t("CARD_NUMBER"),
              expiry: t("EXPIRY"),
              cvc: t("CVC"),
              postalCode: t("POSTAL_CODE"),
              postalPlaceholder: t("POSTAL_CODE_PLACEHOLDER"),
              zipCode: t("ZIP_CODE"),
              zipPlaceholder: t("ZIP_CODE_PLACEHOLDER"),
            }}
          />
        ) : (
          <StripeCardElement onChange={onChange} isDisabled={isDisabled} />
        ))}

      {selectedPaymentType === "direct_debit" && (
        <Text color="text.mid" visual="smallRegular">
          {stripeRegion === "CA"
            ? t("DIRECT_DEBIT_DESCRIPTION_CA")
            : t("DIRECT_DEBIT_DESCRIPTION_US")}
        </Text>
      )}

      {selectedPaymentType === "bank_transfer" && (
        <Text color="text.mid" visual="smallRegular">
          {stripeRegion === "CA"
            ? t("BANK_TRANSFER_DESCRIPTION_CA")
            : t("BANK_TRANSFER_DESCRIPTION_US")}
        </Text>
      )}

      {selectedPaymentType === "offline_payment" && (
        <Flag
          visual="accent"
          hideCloseTrigger
          subText={t("OFFLINE_PAYMENT_DESCRIPTION")}
        />
      )}

      {acceptDirectDebit && isPadOverLimit && stripeRegion === "CA" && (
        <Text color="text.warning.dark" visual="smallRegular">
          {t("PAD_LIMIT_WARNING")}
        </Text>
      )}

      {offerTaxExemption && (
        <Flex
          flexDirection="column"
          gap="150"
          pt="150"
          mt="150"
          borderTop="1px solid"
          borderColor="border.light"
        >
          <Flex justifyContent="space-between" alignItems="center" gap="200">
            <Text visual="bodySemiBold" color="text.dark">
              {t("TAX_EXEMPTION.TOGGLE_LABEL")}
            </Text>
            <Switch
              checked={taxExempt}
              onCheckedChange={e => onTaxExemptToggle(e.checked)}
            />
          </Flex>

          {taxExempt && (
            <Flex flexDirection="column" gap="100">
              <Input
                label={t("TAX_EXEMPTION.NUMBER_LABEL")}
                placeholder={t("TAX_EXEMPTION.NUMBER_PLACEHOLDER")}
                value={taxExemptionNumber}
                onChange={e => onTaxExemptionNumberChange(e.target.value)}
              />
              {!isTaxExemptionNumberValid && (
                <Text color="text.error.dark" visual="smallRegular">
                  {taxExemptionNumber.length > 0
                    ? t("TAX_EXEMPTION.NUMBER_INVALID")
                    : t("TAX_EXEMPTION.NUMBER_REQUIRED")}
                </Text>
              )}
              <Text color="text.light" visual="smallRegular">
                {t("TAX_EXEMPTION.DISCLAIMER")}
              </Text>
            </Flex>
          )}
        </Flex>
      )}
    </CollapsableStep>
  );
};

const PaymentTypeButton = ({
  active,
  onClick,
  label,
  disabled = false,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
}) => (
  <styled.button
    type="button"
    onClick={onClick}
    disabled={disabled}
    display="flex"
    flex="1"
    justifyContent="center"
    alignItems="center"
    py="150"
    borderRadius="100"
    border="2px solid"
    borderColor={active ? "border.dark" : "border.light"}
    bg={active ? "ink.100" : "white"}
    cursor={disabled ? "not-allowed" : "pointer"}
    opacity={disabled ? 0.5 : 1}
    transition="all 0.15s ease"
    _hover={{
      borderColor: "border.dark",
    }}
  >
    <Text
      visual="bodySemiBold"
      color={active ? "text.dark" : "text.mid"}
    >
      {label}
    </Text>
  </styled.button>
);
